import { useEnum } from "@/api/services/global/enum";
import { updateOrderDefaultProductApi, useGetDefaultProductList } from "@/api/services/global/master";
import { TGetDefaultProductListOutputDto } from "@/api/services/global/master/dtos/defaultProduct/get-master-default-product-list.dto";
import { IconOrderTable } from "@/assets/icons/IconOrderTable";
import { ConfirmLeavePage } from "@/components/commons/ConfirmLeavePage";
import { FooterActionBar } from "@/components/commons/FooterActionBar";
import { Button } from "@/components/elements/Button";
import { Checkbox } from "@/components/elements/Checkbox";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { Switch } from "@/components/elements/Switch";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { DATE_TIME_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { DefaultProductFormModal } from "@/features/master-data/components/DefaultProduct/DefaultProductFormModal";
import { HiddenDefaultProductModal } from "@/features/master-data/components/DefaultProduct/HiddenDefaultProductModal";
import { StyledMasterDataContainer, StyledMasterDataDragTable } from "@/features/master-data/components/masterDataTableStyles";
import { MASTER_DATA_TYPE_TITLE } from "@/features/master-data/constants/masterDataType";
import { useVisible } from "@/hooks/useVisible";
import { AppRoutes } from "@/routes/config";
import { blue, text } from "@/theme/colors";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { findLabelByValue } from "@/utils/object";
import { toast } from "@/utils/toast";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { FC, ReactNode, useEffect, useState } from "react";

const TextCell: FC<{ isHidden: boolean; width: string; children: ReactNode }> = ({ isHidden, width, children }) => (
  <TableCell sx={{ width }}>
    <Typography color={isHidden ? text.tertiary : text.primary} variant="body14">
      {children}
    </Typography>
  </TableCell>
);

export const MasterDataDefaultProduct = () => {
  const formModal = useVisible();
  const [hiddenDefaultProduct, setHiddenDefaultProduct] = useState<TGetDefaultProductListOutputDto>();
  const [selectedDefaultProduct, setSelectedDefaultProduct] = useState<TGetDefaultProductListOutputDto>();
  const [enableSaveOrderBtn, setEnableOrderBtn] = useState<boolean>(false);
  const { data, isLoading } = useGetDefaultProductList();
  // 表示ラベルは BE の enum を参照して画面間でぶれないようにする
  const { data: invoiceDisplayFormatOptions } = useEnum("EInvoiceDisplayFormat");
  const { data: billingCycleOptions } = useEnum("EBillingCycle");

  const [rows, setRows] = useState<TGetDefaultProductListOutputDto[]>(data);

  useEffect(() => {
    if (data && data.length > 0) setRows(data);
  }, [data]);

  /** 並び替えのみを保存する。配列の並び順がそのまま新しい表示順序になる */
  const handleSaveOrder = async () => {
    try {
      const rs = await updateOrderDefaultProductApi({ defaultProductIds: rows.map((item) => toNumber(item.id)) });
      setRows(rs.data);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      setEnableOrderBtn(false);
    } catch (error) {
      showError(error);
    }
  };

  // Handle row dragging
  const onDragEnd = (result: DropResult) => {
    if (!result.destination || !result.source || result.source.index === result.destination.index) return;
    const reorderedRows = Array.from(rows);
    const [removed] = reorderedRows.splice(result.source.index, 1);
    reorderedRows.splice(result.destination.index, 0, removed);
    setRows(reorderedRows);
    setEnableOrderBtn(true);
  };

  if (isLoading) return <LoadingPage />;

  return (
    <Stack sx={{ mt: 1 }}>
      <TopPage title={"「" + MASTER_DATA_TYPE_TITLE.DEFAULT_PRODUCT + "」の編集"} backUrl={AppRoutes.masterData} enablePageBack={false} />
      <StyledMasterDataContainer>
        <Stack gap={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={3}>
            {/* お客様の商品マスタが参照している可能性があるため削除は行わない */}
            <Typography variant="cap12" color={text.secondary}>
              お客様の商品マスタが参照しているため行の削除はできません（無効化のみ）。税率は税率マスタから選択し、無効化済みの税率は選択肢に表示されません。
            </Typography>
            <Button size="sm" onClick={() => formModal.open()}>
              項目追加
            </Button>
          </Stack>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="tableRows" direction="vertical">
              {(provided) => (
                <StyledMasterDataDragTable {...provided.droppableProps} ref={provided.innerRef}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell></TableCell>
                        <TableCell>商品名</TableCell>
                        <TableCell>単位</TableCell>
                        <TableCell>税率</TableCell>
                        <TableCell sx={{ textAlign: "center" }}>契約紐付け</TableCell>
                        <TableCell>請求書表示形式</TableCell>
                        <TableCell>請求サイクル</TableCell>
                        <TableCell sx={{ textAlign: "center" }}>ティア・初回割引の対象</TableCell>
                        <TableCell>会計の品目マッピング</TableCell>
                        <TableCell>最終更新日</TableCell>
                        <TableCell sx={{ textAlign: "center" }}>有効／無効</TableCell>
                        <TableCell>編集</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row, index) => (
                        <Draggable key={row.id} draggableId={row.id.toString()} index={index}>
                          {(provided) => (
                            <TableRow ref={provided.innerRef} {...provided.draggableProps}>
                              <TableCell sx={{ width: "3%" }} {...provided.dragHandleProps}>
                                <IconOrderTable color={blue[80]} />
                              </TableCell>
                              <TextCell isHidden={row.isHidden} width="12%">
                                {row.productName || EMPTY_STR.TEXT}
                              </TextCell>
                              <TextCell isHidden={row.isHidden} width="6%">
                                {row.unit || EMPTY_STR.TEXT}
                              </TextCell>
                              {/* GET のレスポンスに税率マスタが同梱されるため追加リクエストは不要 */}
                              <TextCell isHidden={row.isHidden} width="6%">
                                {row.taxRate?.displayName || EMPTY_STR.TEXT}
                              </TextCell>
                              <TableCell sx={{ width: "6%" }} align="center">
                                <Checkbox nonControl checked={row.isContractLinked} disabled />
                              </TableCell>
                              <TextCell isHidden={row.isHidden} width="10%">
                                {findLabelByValue(invoiceDisplayFormatOptions, row.invoiceDisplayFormat) || EMPTY_STR.TEXT}
                              </TextCell>
                              <TextCell isHidden={row.isHidden} width="10%">
                                {findLabelByValue(billingCycleOptions, row.billingCycle) || EMPTY_STR.TEXT}
                              </TextCell>
                              <TableCell sx={{ width: "12%" }} align="center">
                                <Checkbox nonControl checked={row.isTierDiscountTarget} disabled />
                              </TableCell>
                              <TextCell isHidden={row.isHidden} width="12%">
                                {row.accountingItemMapping || EMPTY_STR.TEXT}
                              </TextCell>
                              <TextCell isHidden={row.isHidden} width="10%">
                                {row.updatedAt ? dayjs(row.updatedAt).format(DATE_TIME_FORMAT) : EMPTY_STR.DATE}
                              </TextCell>
                              <TableCell sx={{ width: "6%" }} align="center">
                                <Switch checked={!row.isHidden} onChange={() => setHiddenDefaultProduct(row)} color="primary" />
                              </TableCell>
                              <TableCell sx={{ width: "8%" }} align="center">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedDefaultProduct(row);
                                    formModal.open();
                                  }}
                                >
                                  編集
                                </Button>
                              </TableCell>
                            </TableRow>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </TableBody>
                  </Table>
                </StyledMasterDataDragTable>
              )}
            </Droppable>
          </DragDropContext>
        </Stack>

        {formModal.visible && (
          <DefaultProductFormModal
            onClose={() => {
              formModal.close();
              setSelectedDefaultProduct(undefined);
            }}
            isOpen={formModal.visible}
            selectedDefaultProduct={selectedDefaultProduct}
            defaultProducts={rows}
          />
        )}

        <HiddenDefaultProductModal hiddenDefaultProduct={hiddenDefaultProduct} setHiddenDefaultProduct={setHiddenDefaultProduct} />
        <ConfirmLeavePage shouldBlock={enableSaveOrderBtn}></ConfirmLeavePage>
      </StyledMasterDataContainer>
      <FooterActionBar title="オーダーを保存する" onSubmit={handleSaveOrder} backUrl={AppRoutes.masterData} isDisabled={!enableSaveOrderBtn} />
    </Stack>
  );
};
